import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { type ExpiryExtractionResult } from 'utils/aiExpiryDate.ts';
import './ExpiryPicker.css';

export interface ExpiryPickerProps {
  value: string;
  onChange: (date: string) => void;
  onRequestPhotoCapture?: () => Promise<ExpiryExtractionResult | null>;
}

type PickerMode = 'years' | 'months' | 'days';

const ExpiryPicker: React.FC<ExpiryPickerProps> = ({ value, onChange, onRequestPhotoCapture }) => {
  const [mode, setMode] = useState<PickerMode>('years');
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [lastExtraction, setLastExtraction] = useState<ExpiryExtractionResult | null>(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + i);
  const quickDays = [10, 14, 20];

  const months = [
    { num: 1, name: 'Jan' }, { num: 2, name: 'Feb' }, { num: 3, name: 'Mar' },
    { num: 4, name: 'Apr' }, { num: 5, name: 'May' }, { num: 6, name: 'Jun' },
    { num: 7, name: 'Jul' }, { num: 8, name: 'Aug' }, { num: 9, name: 'Sep' },
    { num: 10, name: 'Oct' }, { num: 11, name: 'Nov' }, { num: 12, name: 'Dec' }
  ];

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  };

  const handleQuickDay = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    onChange(date.toISOString().split('T')[0]);
    setShowPicker(false);
  };

  const handleNeverOverdue = () => {
    onChange('2999-12-31');
    setShowPicker(false);
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setMode('months');
  };

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    setMode('days');
  };

  const handleDaySelect = (day: number) => {
    if (selectedYear && selectedMonth) {
      const dateString = formatDate(selectedYear, selectedMonth, day);
      onChange(dateString);
      // Reset to years view and hide picker
      setMode('years');
      setSelectedYear(null);
      setSelectedMonth(null);
      setShowPicker(false);
    }
  };

  const handleReset = () => {
    setMode('years');
    setSelectedYear(null);
    setSelectedMonth(null);
    onChange('');
    setShowPicker(false);
    setExtractError(null);
    setLastExtraction(null);
  };

  const openCameraCapture = async () => {
    if (!onRequestPhotoCapture) return;

    setExtractError(null);
    setIsExtracting(true);

    try {
      const result = await onRequestPhotoCapture();
      if (!result) return;

      setLastExtraction(result);

      if (result.status !== 'success' || !result.isoDate) {
        const message = result.reason || 'Could not determine an expiry date from that photo.';
        setExtractError(message);
        return;
      }

      onChange(result.isoDate);
      setShowPicker(false);
      toast.success(`Expiry date set to ${result.isoDate}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Expiry extraction failed';
      setExtractError(message);
      setLastExtraction(null);
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <div className="expiry-picker">
      {lastExtraction?.status === 'success' && lastExtraction.isoDate && (
        <div className="expiry-picker__ai-result">
          AI detected {lastExtraction.isoDate}
          {lastExtraction.rawText ? ` from "${lastExtraction.rawText}"` : ''}
        </div>
      )}

      {extractError && (
        <div className="expiry-picker__ai-error">{extractError}</div>
      )}

      {(!value || showPicker) && (
        <>
          {mode === 'years' && (
            <div className="expiry-picker__section">
              {/* Quick Days */}
              <div className="expiry-picker__quick-days">
                {quickDays.map(days => (
                  <button
                    key={days}
                    onClick={() => handleQuickDay(days)}
                    className="expiry-picker__quick-day"
                  >
                    +{days} days
                  </button>
                ))}
                <button
                  onClick={handleNeverOverdue}
                  className="expiry-picker__quick-day expiry-picker__never-overdue"
                >
                  ∞
                </button>
                <button
                  type="button"
                  onClick={openCameraCapture}
                  className="expiry-picker__quick-day expiry-picker__camera-btn"
                  disabled={isExtracting || !onRequestPhotoCapture}
                  aria-label={isExtracting ? 'Reading expiry date photo' : 'Take photo of expiry date'}
                  title={isExtracting ? 'Reading expiry date photo' : 'Take photo of expiry date'}
                >
                  {isExtracting ? (
                    <span className="expiry-picker__camera-loading">...</span>
                  ) : (
                    <svg
                      className="expiry-picker__camera-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M8 6.5 9.4 5h5.2L16 6.5h2.8A2.2 2.2 0 0 1 21 8.7v8.1A2.2 2.2 0 0 1 18.8 19H5.2A2.2 2.2 0 0 1 3 16.8V8.7A2.2 2.2 0 0 1 5.2 6.5H8Zm4 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0 1.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Years */}
              <div className="expiry-picker__years">
                {years.map(year => (
                  <button
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    className="expiry-picker__year"
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === 'months' && selectedYear && (
            <div className="expiry-picker__section">
              <div className="expiry-picker__title">
                Select Month for {selectedYear}
                <button onClick={() => setMode('years')} className="expiry-picker__back">← Back</button>
              </div>
              <div className="expiry-picker__months">
                {months.map(month => (
                  <button
                    key={month.num}
                    onClick={() => handleMonthSelect(month.num)}
                    className="expiry-picker__month"
                  >
                    {month.num.toString().padStart(2, '0')} - {month.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === 'days' && selectedYear && selectedMonth && (
            <div className="expiry-picker__section">
              <div className="expiry-picker__title">
                Select Day for {selectedYear}-{selectedMonth.toString().padStart(2, '0')}
                <button onClick={() => setMode('months')} className="expiry-picker__back">← Back</button>
              </div>
              <div className="expiry-picker__days">
                {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => i + 1).map(day => (
                  <button
                    key={day}
                    onClick={() => handleDaySelect(day)}
                    className="expiry-picker__day"
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {value && (
        <div className="expiry-picker__selected">
          Selected: {value}
          <div className="expiry-picker__selected-actions">
            <button type="button" onClick={() => setShowPicker((current) => !current)} className="expiry-picker__clear">
              {showPicker ? 'Hide manual' : 'Edit manually'}
            </button>
            <button type="button" onClick={handleReset} className="expiry-picker__clear">Clear</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiryPicker; 
